import allCountries from '@rover/constants/src/geography/countries.constants.json';
import { ACTIVE_COUNTRY_CODES } from '@rover/utilities/roverGeo.constants';

const DEFAULT_LOCALE = 'en-us';

/**
 * Get localized country names for the given locale
 * Falls back to English if locale file doesn't exist or fails to load
 */
export const getLocalizedCountries = async (locale: string): Promise<Record<string, string>> => {
  if (locale === DEFAULT_LOCALE) {
    return {};
  }

  try {
    // Use conditional static imports for all available locales
    // Metro bundler can analyze these at build time
    const localeImporters: Record<string, () => Promise<Record<string, unknown>>> = {
      'de-de': () => import('@rover/app/locale/de-de/countries.json'),
      'es-es': () => import('@rover/app/locale/es-es/countries.json'),
      'fr-ca': () => import('@rover/app/locale/fr-ca/countries.json'),
      'fr-fr': () => import('@rover/app/locale/fr-fr/countries.json'),
      'it-it': () => import('@rover/app/locale/it-it/countries.json'),
      'nb-no': () => import('@rover/app/locale/nb-no/countries.json'),
      'nl-nl': () => import('@rover/app/locale/nl-nl/countries.json'),
      'sv-se': () => import('@rover/app/locale/sv-se/countries.json'),
    };

    const importer = localeImporters[locale];
    if (!importer) {
      return {};
    }

    const localeModule = await importer();

    // Handle both default export and direct export patterns
    return (localeModule.default || localeModule) as Record<string, string>;
  } catch (error: unknown) {
    // This will cause the component to fall back to English names
    return {};
  }
};

/**
 * Get country options for a select field, filtered to only include countries where Rover operates
 * Uses localized country names when available
 */
export const getCountryOptions = async (
  locale: string = DEFAULT_LOCALE
): Promise<Array<{ value: string; label: string }>> => {
  const localizedCountries = await getLocalizedCountries(locale);

  // Filter to only countries where Rover operates
  const roverCountries = allCountries.filter((country) =>
    ACTIVE_COUNTRY_CODES.includes(country.code as (typeof ACTIVE_COUNTRY_CODES)[number])
  );

  // Map to select field options, using localized names when available
  return roverCountries.map((country) => ({
    value: country.code,
    label: localizedCountries[country.code.toLowerCase()] || country.name,
  }));
};
